import { useCallback, useEffect, useState } from "react";
import { useParams } from 'react-router-dom';
import Editor from "../components/ProductDetails/Editor.js";
import config from "../constants/config.js";
import EditorLoading from "../components/ProductDetails/EditorLoading.js";

function ProductsDetailsView() {
    const { handle } = useParams();
    const [ product , setProduct ] = useState( {} );
    const [ variants , setVariants ] = useState( [] );
    const [ shops , setShops ] = useState( [] );
    const [ themes , setThemes ] = useState( [] );
    const [ theme , setTheme ] = useState( '' );
    const [ views , setViews ] = useState( [] );
    const [ view , setView ] = useState( '' );
    const [ savedTemplates , setSavedTemplates ] = useState( [] );
    const [ personalizationOptions , setPersonalizationOptions ] = useState( [] );
    const [ layerConfig , setLayerConfig ] = useState( null );
    const [ loadingData , setLoadingData ] = useState( true );
    
    useEffect( () => {
        async function load() {
            setLoadingData( true );

            /**
             * Load Products. Currently uses handle in URL to pull in all related products by handle
             */
            const _product = await getProduct();
            if ( _product != null ) {
                setProduct( _product );
                setVariants( _product?.variants[ _product.related_shopify_ids[0] ] || []);
            }

            /**
             * Load Themes. Currently support 2 themes
             */
            const _themes = await getThemes();
            if ( _themes.length > 0 ) {
                setThemes( _themes );
                setTheme( _themes[0].handle );
            }

            /**
             * Load Views. Any new views must be mapped in Editor.js
             */
            const _views = await getViews();
            if ( _views.length > 0 ) {
                setViews( _views );
                setView( _views[0].handle )
            }

            /**
             * Load Personalization Options. 
             */
            const _personalizationOptions = await getPersonalizationOptions();
            if ( _personalizationOptions.length > 0 ) {
                setPersonalizationOptions( _personalizationOptions );
            }

            /**
             * Load Product Layers. Currently uses handle in URL to pull in layers
             */
            const _layers = await getLayers();
            if ( _layers != null  ) {
                const _layersPatched = ensureConfigForVariants( _product?.variants[ _product.related_shopify_ids[0] ] , _layers );
                setLayerConfig( _layersPatched );
            }

            /**
             * Load shoplist from server
             */
            const _shops = await getShopList();
            if ( _shops.length > 0 ) {
                setShops( _shops );
            }

            const _templates = await getSavedTemplates();
            if ( _templates.length > 0 ) {
                setSavedTemplates( _templates );
            } 

            setLoadingData( false );
        }

        load();
    } , [] );

    async function getThemes() {
        let themesFetched = [];

        try {
            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/themes`;
            const requestSettings = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            };
            const response = await fetch( url , requestSettings );
            const data = await response.json();

            if ( data?.themes?.length > 0 ) {
                themesFetched = data.themes;
            }
        } catch ( error ) {
            console.log( error );
        }

        return themesFetched;
    }

    async function getViews() {
        let viewsFetched = [];

        try {
            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/views`;
            const requestSettings = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            };
            const response = await fetch( url , requestSettings );
            const data = await response.json();

            if ( data?.views?.length > 0 ) {
                viewsFetched = data.views;
            }
        } catch ( error ) {
            console.log( error );
        }

        return viewsFetched;
    }

    async function getProduct() {
        let productFetched = null;

        try {
            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/products/${encodeURIComponent( handle )}`;
            const requestSettings = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            };
            const response = await fetch( url , requestSettings );
            const data = await response.json();

            if ( data?.product != null ) {
                productFetched = data.product;
            }
        } catch ( error ) {
            console.log( error );
        }

        return productFetched;
    }

    async function getPersonalizationOptions() {
        let options = [];

        try {
            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/personalization/options`;
            const requestSettings = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            };
            const response = await fetch( url , requestSettings );
            const data = await response.json();

            if ( data?.personalizationOptions != null && data?.personalizationOptions?.length > 0 ) {
                options = data.personalizationOptions;
            }
        } catch ( error ) {
            console.log( error );
        }

        return options;
    }

    async function getLayers() {
        let layers = null;

        try {
            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/products/${encodeURIComponent( handle )}/personalization`;
            const requestSettings = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            };
            const response = await fetch( url , requestSettings );
            const data = await response.json();

            if ( data?.layers != null ) {
                layers = data.layers;
            }
        } catch ( error ) {
            console.log( error );
        }

        return layers;
    }

    async function getShopList() {
        let shops = [];

        try {
            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/shops`;
            const requestSettings = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            };
            const response = await fetch( url , requestSettings );
            const data = await response.json();

            if ( data?.shopList != null && data?.shopList?.length > 0 ) {
                shops = data.shopList;

            }
        } catch ( error ) {
            console.log( error );
        }

        return shops;
    }

    async function getSavedTemplates() {
        let savedTemplates = [];

        try {
            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/templates`;
            const requestSettings = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            };
            const response = await fetch( url , requestSettings );
            const data = await response.json();

            if ( data?.templates != null && data?.templates?.length > 0 ) {
                savedTemplates = data.templates;
            }
        } catch ( error ) {
            console.log( error );
        }

        return savedTemplates;
    }

    function ensureConfigForVariants( variants , layerConfig ) {
        const updated = structuredClone( layerConfig );

        if (!updated.front) { 
            updated.front = {};
        }

        if (!updated.back) { 
            updated.back = {}; 
        }

        for ( let i = 0; i < variants.length; i++ ) {
            const handle = variants[i].value;

            if (!updated.front[handle]) {
                updated.front[handle] = [];
            }

            if (!updated.back[handle]) {
                updated.back[handle] = [];
            }
        }

        return updated;
    }

    const toggleTheme = useCallback( () => {
        if ( theme === themes[0].handle ) {
            setTheme( themes[1].handle )
        } else {
            setTheme( themes[0].handle )
        }
    } , [ theme , themes ] );

    const toggleView = useCallback( ( newView ) => {
        setView( newView );
    } ,  [ ] );

    return(
        <div data-theme={theme} className="product-details-view">
            { loadingData && <EditorLoading /> }
            { !loadingData && 
                <Editor 
                    product={ product }
                    variants={ variants }
                    shops={ shops }
                    toggleTheme={ toggleTheme }
                    theme={ theme }
                    toggleView={ toggleView }
                    view={ view }
                    views={ views }
                    personalizationOptions={ personalizationOptions }
                    layerConfig={ layerConfig }
                    setLayerConfig={ setLayerConfig }
                    savedTemplates={ savedTemplates }
                /> 
            }
        </div>
    );
}

export default ProductsDetailsView;